/**
 * Classification item review — accept / reject AI proposal, set classification,
 * and optionally mark AI-authored content as human-reviewed (Doc 30).
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import {
  AppCard,
  EmptyState,
  Eyebrow,
  PrimaryButton,
  SecondaryButton,
  StatusChip,
} from '../components/primitives';
import { CreationProvenanceBadge } from '../../components/prioritization/provenance/CreationProvenanceBadge';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useItemClassificationStore } from '../../stores/prioritization/itemClassificationStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { approveAiItemRpc } from '../../lib/itemProvenanceRpc';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { nextClassificationItemId } from './classificationQueue';
import { MARK_REVIEWED_LABEL, MARK_REVIEWED_REFUSED_LINE, MARKING_REVIEWED_LABEL } from '../../lib/items/reviewVocabulary';

const CATEGORIES = [
  'problem',
  'opportunity',
  'risk',
  'solution',
  'commitment',
  'investment',
] as const;

export default function ClassificationItemScreen() {
  const app = getAppDefinition('classification')!;
  const { itemId = '' } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const item = useItemsStore((s) => s.items.find((i) => i.id === itemId));
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const updateItem = useItemsStore((s) => s.updateItem);

  const state = useItemClassificationStore((s) => s.byItem[itemId]);
  const fetchForItem = useItemClassificationStore((s) => s.fetchForItem);
  const accept = useItemClassificationStore((s) => s.accept);
  const reject = useItemClassificationStore((s) => s.reject);
  const setDirect = useItemClassificationStore((s) => s.setDirect);
  const mutating = state?.mutating ?? false;

  const [category, setCategory] = useState<string>('problem');
  const [altitude, setAltitude] = useState(3);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  // What just succeeded — drives the next-step card (next item / back to queue).
  const [settled, setSettled] = useState<string | null>(null);
  const items = useItemsStore((s) => s.items);
  const nextItemId = settled ? nextClassificationItemId(items, itemId) : null;
  // approve_ai_item's authority: workspace owner/admin or items.approve_ai_provenance.
  const can = useUserStore((s) => s.can);
  // can() reads the store, not React state: follow permissionsLoaded so this redraws when rights arrive.
  useUserStore((s) => s.permissionsLoaded);
  const canMarkReviewed = can('items.approve_ai_provenance');

  useEffect(() => {
    setSettled(null);
    setActionError(null);
  }, [itemId]);

  useEffect(() => {
    if (!item) void fetchItems();
  }, [item, fetchItems]);

  useEffect(() => {
    if (itemId) void fetchForItem(itemId);
  }, [itemId, fetchForItem]);

  useEffect(() => {
    const proposal = state?.activeProposal;
    if (proposal) {
      setCategory(proposal.classificationFields.category);
      setAltitude(proposal.classificationFields.altitudeLevel);
    } else if (item?.itemCategory) {
      setCategory(item.itemCategory);
    }
    if (item?.altitudeLevel) setAltitude(item.altitudeLevel);
  }, [state?.activeProposal, item]);

  const proposal = state?.activeProposal ?? null;
  const status = item?.classificationStatus ?? 'empty';
  const needsAuthorshipReview =
    (item?.creationSource === 'ai' || item?.creationSource === 'automated') &&
    item?.creationReviewStatus !== 'human_reviewed';

  const onAccept = async () => {
    if (!proposal || !userId) return;
    setActionError(null);
    const result = await accept({ proposalId: proposal.id, itemId, userId });
    if (!result.success) {
      setActionError(result.error ?? 'Accept failed');
      return;
    }
    await fetchItems();
    setSettled('Proposal accepted.');
  };

  const onReject = async () => {
    if (!proposal) return;
    setActionError(null);
    const result = await reject({ proposalId: proposal.id, itemId });
    if (!result.success) {
      setActionError(result.error ?? 'Reject failed');
      return;
    }
    await fetchItems();
    setSettled('Proposal rejected.');
  };

  const onSetDirect = async () => {
    if (!userId) return;
    if (!getCurrentWorkspaceId()) {
      setActionError('No workspace context');
      return;
    }
    setActionError(null);
    const result = await setDirect({
      itemId,
      userId,
      category,
      altitudeLevel: altitude,
    });
    if (!result.success) {
      setActionError(result.error ?? 'Save failed');
      return;
    }
    await fetchItems();
    setSettled('Classification saved.');
  };

  const onMarkReviewed = async () => {
    if (!userId || !item) return;
    setReviewBusy(true);
    setActionError(null);
    const result = await approveAiItemRpc(item.id, userId);
    setReviewBusy(false);
    if (!result.success) {
      setActionError(result.error ?? 'Could not mark as reviewed');
      return;
    }
    updateItem(item.id, {
      creationReviewStatus: 'human_reviewed',
      creationProvenance: 'ai_approved',
    });
    await fetchItems();
  };

  if (!itemId) {
    return <NavigateHome />;
  }

  if (!item && !state?.loading) {
    return (
      <AppShell app={app} backTo="/apps/classification" backLabel="Back to queue">
        <EmptyState
          title="Item not found"
          hint="It may have been removed or is outside this workspace."
          action={
            <SecondaryButton onClick={() => navigate('/apps/classification')}>
              Back to queue
            </SecondaryButton>
          }
        />
      </AppShell>
    );
  }

  return (
    <AppShell app={app} backTo="/apps/classification" backLabel="Back to queue">
      <ScreenHeading
        title={item?.title ?? 'Item'}
        subtitle="Confirm classification. Authorship and classification are separate axes."
      />
      <BallLine ballWith="you — confirm or override" />

      <div className="space-y-4">
        {settled && (
          <AppCard>
            <Eyebrow>Done</Eyebrow>
            <p className="mt-2 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
              {settled} {nextItemId ? 'Another item is waiting.' : 'Nothing else is waiting in the queue.'}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {nextItemId && (
                <PrimaryButton onClick={() => navigate(`/apps/classification/${nextItemId}`)}>
                  Next item
                </PrimaryButton>
              )}
              <SecondaryButton onClick={() => navigate('/apps/classification')}>
                Back to queue
              </SecondaryButton>
            </div>
          </AppCard>
        )}
        <AppCard>
          <Eyebrow>Status</Eyebrow>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusChip
              label={
                status === 'approved'
                  ? 'Approved'
                  : status === 'proposed'
                    ? 'Proposed'
                    : 'Unclassified'
              }
              tone={status === 'approved' ? 'green' : status === 'proposed' ? 'mustard' : 'neutral'}
            />
            {proposal?.proposedBy === 'agent' && (
              <StatusChip label="AI proposal" tone="slate" />
            )}
            {proposal?.proposedBy === 'user' && (
              <StatusChip label="Human proposal" tone="neutral" />
            )}
            <CreationProvenanceBadge
              source={item?.creationSource}
              reviewStatus={item?.creationReviewStatus}
            />
          </div>
          {item?.description ? (
            <p className="mt-3 font-body text-sm text-gray-600 dark:text-[var(--text-sec)] line-clamp-4">
              {item.description}
            </p>
          ) : null}
        </AppCard>

        {proposal && (
          <AppCard>
            <Eyebrow>Open proposal</Eyebrow>
            <dl className="mt-2 space-y-1 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
              <div>
                <span className="text-gray-500">Category: </span>
                {proposal.classificationFields.category}
              </div>
              <div>
                <span className="text-gray-500">Altitude: </span>
                {proposal.classificationFields.altitudeLevel}
              </div>
              {proposal.classificationFields.executionProfile && (
                <div>
                  <span className="text-gray-500">Execution: </span>
                  {proposal.classificationFields.executionProfile}
                </div>
              )}
              {proposal.classificationFields.rationale && (
                <p className="pt-2 text-gray-600 dark:text-[var(--text-ter)]">
                  {proposal.classificationFields.rationale}
                </p>
              )}
            </dl>
            <div className="mt-4 flex flex-wrap gap-2">
              <PrimaryButton onClick={() => void onAccept()} disabled={mutating || !userId}>
                Accept proposal
              </PrimaryButton>
              <SecondaryButton onClick={() => void onReject()} disabled={mutating || !userId}>
                Reject
              </SecondaryButton>
            </div>
          </AppCard>
        )}

        <AppCard>
          <Eyebrow>{proposal ? 'Or set manually' : 'Set classification'}</Eyebrow>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block font-body text-sm">
              <span className="text-gray-500 dark:text-[var(--text-ter)]">Category</span>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="block font-body text-sm">
              <span className="text-gray-500 dark:text-[var(--text-ter)]">Altitude (1–5)</span>
              <select
                value={altitude}
                onChange={(e) => setAltitude(Number(e.target.value))}
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-4">
            <PrimaryButton onClick={() => void onSetDirect()} disabled={mutating || !userId}>
              Save classification
            </PrimaryButton>
          </div>
        </AppCard>

        {needsAuthorshipReview && (
          <AppCard>
            <Eyebrow>Creation provenance</Eyebrow>
            <p className="mt-2 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
              This item is AI-generated (or automated) and has not been marked human-reviewed.
              Classification confirmation does not automatically endorse authorship.
            </p>
            {/* §2.8: not drawn for someone approve_ai_item would refuse; the line instead. */}
            {canMarkReviewed ? (
              <div className="mt-4">
                <SecondaryButton
                  onClick={() => void onMarkReviewed()}
                  disabled={reviewBusy || !userId}
                >
                  {reviewBusy ? MARKING_REVIEWED_LABEL : MARK_REVIEWED_LABEL}
                </SecondaryButton>
              </div>
            ) : (
              <p className="mt-3 font-body text-sm text-gray-500 dark:text-[var(--text-ter)]" data-testid="mark-reviewed-refused">
                {MARK_REVIEWED_REFUSED_LINE}
              </p>
            )}
          </AppCard>
        )}

        {actionError && (
          <p className="font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
            {actionError}
          </p>
        )}

        <p className="font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
          Full detail:{' '}
          <Link
            to={`/prioritization/items/${itemId}`}
            className="text-primary-600 hover:underline dark:text-primary-400"
          >
            Open item
          </Link>
        </p>
      </div>
    </AppShell>
  );
}

function NavigateHome() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/apps/classification', { replace: true });
  }, [navigate]);
  return null;
}
